// Loading the board, and every write it makes.
//
// The working board and one bounded Complete page arrive in the first
// `GET /board`, so nothing here assembles the initial screen from several calls.
// Older Complete pages are fetched only when the disclosure asks for them.
//
// NOTHING HERE IS A NEW KIND OF WRITE. Moving a card is `PATCH /routines/:id`
// with the column's own status; finishing one is the completion the day screen
// ticks with, and taking it back out is that completion's delete with the tuple
// in the QUERY STRING; making one is `POST /routines` with no schedule, which is
// what a reminder is; archiving one is `DELETE /routines/:id`, undone by a
// PATCH. The board is a view, and its writes are the ones that were already
// there (api/src/routes/board.routes.ts).

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { BoardResponse } from '../api/client.ts';
import {
  addedNotice,
  archivedNotice,
  columnAt,
  findCard,
  movePlan,
  movedNotice,
  restoredNotice,
  withCardMoved,
  withCardRestored,
  withOlderCompletePage,
} from './board-model.ts';

export interface BoardHost {
  /**
   * Whether the board is the screen that is on.
   *
   * IT IS MOUNTED FROM `App.tsx` WHATEVER SCREEN IS ON - so that a board read
   * once survives a look at Today and back, the way the day's own cache does -
   * and this is what stops that costing a request nobody asked for. A person who
   * never opens the board never fetches one, and the day screen's first paint is
   * not waiting behind a second read. It re-reads on returning, keeping whatever
   * it last had on screen while it does, so there is no flash of nothing.
   */
  active: boolean;
  /** The shell's toast, and the way back from a destructive line on it. */
  say: (message: string, undo?: () => void) => void;
  dismiss: () => void;
  /**
   * Said after every board write that reached the server, so the rest of the
   * shell catches up with it.
   *
   * THE BOARD'S OWN `reload()` IS NOT THIS. That one re-reads `GET /board` and
   * nothing else, and it is the whole reason this hook looked correct for as
   * long as it did: the board is `active`-gated, so it re-reads on every trip to
   * the tab, and a completion written anywhere else therefore turns up here
   * without anyone having said so. Nothing else in this shell works that way.
   * The day's payload is cached per day and re-read only when `revision` moves
   * (`useDayScreen`), and the month and the year file a successful read with
   * `visit: null` so it outlives the trip that asked for it - so a board write
   * that says nothing leaves the reminders block on Today drawing a card as open
   * that this screen has just drawn in Complete, and leaves Progress counting a
   * finished thing whose completion row is gone. Reported by the captain on
   * 2026-08-19 for the reminders block; the same silence was making the other
   * two wrong.
   *
   * SAID FOR EVERY WRITE, not only the ones that touch a completion. A card is a
   * reminder, so making, archiving and restoring one changes the day's block as
   * surely as finishing one does - and which of the remaining fields another
   * screen happens to draw today is not a distinction this hook should be
   * carrying. `useEditor` refreshes after every write for the same reason.
   */
  refresh: () => void;
  /** Any 401, from this screen's own read or from any of its writes. */
  onSignedOut: () => void;
}

export interface BoardScreenState {
  board: BoardResponse | null;
  loading: boolean;
  /**
   * Set only when the board could not be read. A 401 is NOT one of these: it is
   * a state of the session rather than of the board, and it raises the
   * signed-out screen instead - otherwise "Try again" re-sends the same
   * sessionless request for ever (web/src/README.md).
   */
  failure: string | null;
  /** Cards with a write in flight, so a control cannot be pressed twice. */
  busy: ReadonlySet<string>;
  /** Columns with a create in flight, for the same reason. */
  adding: ReadonlySet<string>;
  /** One older Complete page is in flight. */
  loadingOlder: boolean;
  completePagingReady: boolean;
  loadOlder: (cursor: string) => void;
  move: (cardId: string, toKey: string) => void;
  /**
   * Resolves TRUE only once the card is on the server.
   *
   * The composer waits on it and keeps itself - and the typed title - open until
   * then, so a create that did not land can be pressed again rather than being
   * retyped. Losing somebody's words to a failed write is the same defect as a
   * write that succeeds invisibly: either way they cannot tell what happened and
   * either way they do it twice (web/src/README.md).
   */
  add: (columnKey: string, title: string) => Promise<boolean>;
  archive: (cardId: string) => void;
  restore: (cardId: string) => void;
  retry: () => void;
}

export function useBoard(host: BoardHost): BoardScreenState {
  const [board, setBoard] = useState<BoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  const [adding, setAdding] = useState<ReadonlySet<string>>(() => new Set());
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [completePagingReady, setCompletePagingReady] = useState(false);
  const olderInFlight = useRef(false);
  const olderRequest = useRef(0);
  const completePagingReadyRef = useRef(false);
  const completeMutations = useRef(0);

  // Read through a ref so the effect below depends on WHICH read is wanted and
  // not on how the caller happens to be rendered: `say` and the rest are fresh
  // closures every render, and depending on them would re-read the board on
  // every keystroke anywhere in the shell (the same shape `SettingsScreen`
  // needs, and for the same reason).
  const latest = useRef(host);
  latest.current = host;

  // The board as the last render saw it, for the callbacks below: they are
  // stable, and a callback that closed over `board` would either be rebuilt on
  // every card move or act on a board several moves old.
  const current = useRef<BoardResponse | null>(board);
  current.current = board;

  const { active } = host;

  const invalidateOlder = useCallback(() => {
    olderRequest.current += 1;
    olderInFlight.current = false;
    completePagingReadyRef.current = false;
    setLoadingOlder(false);
    setCompletePagingReady(false);
  }, []);

  useEffect(() => {
    invalidateOlder();
    // Nothing is read for a screen nobody is looking at. See `BoardHost.active`.
    if (!active) return;

    let live = true;
    const pagingRequest = olderRequest.current;
    setLoading(true);
    void (async () => {
      try {
        const payload = await api.board();
        if (!live) return;
        setBoard(payload);
        if (olderRequest.current === pagingRequest && completeMutations.current === 0) {
          completePagingReadyRef.current = true;
          setCompletePagingReady(true);
        }
        setFailure(null);
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current.onSignedOut();
          // Left as a failure even so, so the effect does not fire again on a
          // board that will never arrive.
          if (live) setFailure(messageFor(error));
          return;
        }
        if (live) setFailure(messageFor(error));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [active, generation, invalidateOlder]);

  const reload = useCallback(() => {
    invalidateOlder();
    setLoading(true);
    setGeneration((n) => n + 1);
  }, [invalidateOlder]);

  const held = useCallback(async <T>(cardId: string, work: () => Promise<T>): Promise<T> => {
    setBusy((ids) => new Set(ids).add(cardId));
    try {
      return await work();
    } finally {
      setBusy((ids) => {
        const next = new Set(ids);
        next.delete(cardId);
        return next;
      });
    }
  }, []);

  /**
   * A write reached the server: re-read the board, and tell the shell.
   *
   * The two halves are not the same thing and the second one is the one that
   * was missing. `reload()` refreshes `GET /board` - this screen's own picture -
   * while `refresh()` is what reaches the day's cached payload, the reminders
   * list, the month and the year, none of which re-read on their own. The
   * reasoning is at `BoardHost.refresh`.
   */
  const wrote = useCallback(() => {
    reload();
    latest.current.refresh();
  }, [reload]);

  /** Every 401 from a write lands on the same screen a 401 from a read does. */
  const report = useCallback((prefix: string, error: unknown) => {
    if (isSignedOut(error)) {
      latest.current.onSignedOut();
      return;
    }
    latest.current.say(`${prefix} ${messageFor(error)}`);
  }, []);

  const loadOlder = useCallback(
    (cursor: string) => {
      if (loading || olderInFlight.current || !completePagingReadyRef.current) return;
      olderInFlight.current = true;
      const request = olderRequest.current + 1;
      olderRequest.current = request;
      setLoadingOlder(true);

      void api
        .board(cursor)
        .then((page) => {
          if (olderRequest.current !== request) return;
          setBoard((stale) => {
            if (olderRequest.current !== request || stale === null) return stale;
            const complete = stale.columns.find((column) => column.completes);
            // A re-read or another page may have replaced this position while
            // the request was in flight. Appending it there would duplicate or
            // skip history, so only the board that asked may take the answer.
            if (complete?.nextCursor !== cursor) return stale;
            return withOlderCompletePage(stale, page);
          });
        })
        .catch((error: unknown) => {
          if (olderRequest.current === request) {
            report('Older completed cards were not loaded.', error);
          }
        })
        .finally(() => {
          if (olderRequest.current !== request) return;
          olderInFlight.current = false;
          setLoadingOlder(false);
        });
    },
    [loading, report],
  );

  const move = useCallback(
    (cardId: string, toKey: string) => {
      const now = current.current;
      if (now === null) return;

      const at = findCard(now, cardId);
      const to = columnAt(now, toKey);
      if (at === null || to === null) return;

      const steps = movePlan(at, to, now.today);
      // A card dropped back where it started is not a request, and not a line on
      // the toast either - saying "moved to To-do" about a card that did not
      // move is worse than saying nothing.
      if (steps.length === 0) return;

      const from = at.column;
      const changesComplete = from.completes || to.completes;
      if (changesComplete) {
        completeMutations.current += 1;
        invalidateOlder();
      }
      // The card moves first and goes back if the write does not land, which is
      // the trade `useDayScreen` makes for a tick: a drag that waited for a
      // round trip before the card moved would feel broken.
      setBoard(withCardMoved(now, cardId, toKey));

      void held(cardId, async () => {
        // How many of the plan's writes the server has taken, and whether one of
        // them disagreed with the board this screen was drawing. Either answer
        // means the optimistic picture can no longer be repaired by hand and has
        // to be re-read - see the two branches below.
        let landed = 0;
        let diverged = false;
        try {
          for (const step of steps) {
            // Sequential, not concurrent: leaving Complete deletes the
            // completion and THEN writes the column, and a status landing
            // before the un-tick would be a card in two columns for as long as
            // the second request took.
            if (step.op === 'complete') await api.complete(cardId, step.day);
            else if (step.op === 'uncomplete') {
              // `deleted: false` means the completion this card said it carried
              // was not there, so the board on screen was already older than the
              // server. The move itself still stands - the status below is what
              // was asked for - but the picture it was planned from does not.
              const { deleted } = await api.uncomplete(cardId, step.day);
              if (!deleted) diverged = true;
            } else await api.updateRoutine(cardId, { status: step.status });
            landed += 1;
          }
          if (changesComplete || diverged) reload();
          // Says where it went, always. On a phone the columns scroll sideways,
          // so the one a card was just moved to is routinely off screen when it
          // gets there - which is this client's oldest defect wearing a board's
          // clothes (web/src/README.md).
          latest.current.say(movedNotice(at.card.title, to));
        } catch (error) {
          if (landed === 0) {
            // Nothing was written, so the column the card came from is still the
            // best guess this screen has and putting it back is instant. It is a
            // GUESS and not an answer, which is what the re-read below settles:
            // a refusal is very often the server saying the card was never where
            // this board drew it.
            setBoard((stale) =>
              stale === null ? stale : withCardRestored(stale, at.card, from.key),
            );
          }
          // A MOVE THAT FAILED RECONCILES, whether one write landed or none.
          // Half a plan leaves no column the card can honestly be put back into
          // - the completion is already gone, and a card restored to Complete
          // would be drawn struck through and "Finished 4 August" when the
          // server holds neither. And a refusal is itself news about the card:
          // `already-finished` means it was ticked somewhere else, so putting it
          // back in To-do draws it open and un-struck while the server holds it
          // in Complete, and the refusal's own advice - untick the day it was
          // finished on - names the one thing this screen cannot do to a card it
          // is not drawing in Complete.
          reload();
          report('Not moved.', error);
        } finally {
          if (changesComplete) completeMutations.current -= 1;
          // HOWEVER IT ENDED, and this is the one write here that has to say so
          // from a `finally`: a move is a PLAN, so a refusal part-way through
          // still leaves whatever the server already took - typically the
          // un-tick of a card being dragged out of Complete, with only its
          // status write refused. `landed` is the count of steps the server
          // actually accepted, so it is the honest test of whether anything
          // outside this screen has gone stale.
          if (landed > 0) latest.current.refresh();
        }
      });
    },
    [held, invalidateOlder, reload, report],
  );

  const add = useCallback(
    async (columnKey: string, title: string): Promise<boolean> => {
      const now = current.current;
      const column = now === null ? null : columnAt(now, columnKey);
      if (column === null) return false;

      const trimmed = title.trim();
      if (trimmed === '') return false;

      setAdding((keys) => new Set(keys).add(columnKey));
      try {
        // `status` is the column's own, sent back exactly as it arrived, so
        // this screen never has to know that To-do is a null. A card opens in
        // the column its composer is in, which is the only place it could
        // sensibly appear - so there is no "added, and it is somewhere else"
        // case here, and the line still says which column it went to.
        await api.createCard({ title: trimmed, status: column.status });
        wrote();
        latest.current.say(addedNotice(trimmed, column));
        return true;
      } catch (error) {
        report('Not added.', error);
        return false;
      } finally {
        setAdding((keys) => {
          const next = new Set(keys);
          next.delete(columnKey);
          return next;
        });
      }
    },
    [wrote, report],
  );

  const restore = useCallback(
    (cardId: string) => {
      const changesComplete =
        (current.current?.archived.find((card) => card.id === cardId)?.completedDay ?? null) !==
        null;
      if (changesComplete) {
        completeMutations.current += 1;
        invalidateOlder();
      }
      void held(cardId, async () => {
        try {
          const routine = await api.updateRoutine(cardId, { archived: false });
          wrote();
          latest.current.say(restoredNotice(routine.title));
        } catch (error) {
          if (changesComplete) reload();
          report('Not restored.', error);
        } finally {
          if (changesComplete) completeMutations.current -= 1;
        }
      });
    },
    [held, invalidateOlder, reload, wrote, report],
  );

  const archive = useCallback(
    (cardId: string) => {
      const now = current.current;
      const at = now === null ? null : findCard(now, cardId);
      if (at === null) return;
      const title = at.card.title;
      const changesComplete = at.column.completes;
      if (changesComplete) {
        completeMutations.current += 1;
        invalidateOlder();
      }

      void held(cardId, async () => {
        try {
          await api.archiveRoutine(cardId);
          wrote();
          // Undo, because archiving is the destructive action on this screen and
          // undo on one is a correctness requirement in this product rather than
          // a feature (AGENTS.md). It takes its own line away as it runs: a
          // button left up while its request is in flight is one that can be
          // pressed twice, and the second press is reported as a failure for an
          // undo that worked (web/src/README.md).
          latest.current.say(archivedNotice(title), () => {
            latest.current.dismiss();
            restore(cardId);
          });
        } catch (error) {
          if (changesComplete) reload();
          report('Not archived.', error);
        } finally {
          if (changesComplete) completeMutations.current -= 1;
        }
      });
    },
    [held, invalidateOlder, reload, wrote, report, restore],
  );

  const retry = useCallback(() => {
    reload();
  }, [reload]);

  return {
    board,
    loading,
    failure,
    busy,
    adding,
    loadingOlder,
    completePagingReady,
    loadOlder,
    move,
    add,
    archive,
    restore,
    retry,
  };
}
